import type { Success } from '@1webit/cvs-api-scheme/models';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import type { ToasterToast } from '@/components/ui/use-toast';
import { toast } from '@/components/ui/use-toast';
import type { FormActionError, ServerActionError } from '@/utils/get-response';
import { errorToString } from '@/utils/get-response';

export const handleFormError = <TFormSchema extends FieldValues>(
  error: unknown,
  form: UseFormReturn<TFormSchema>,
  errorToastMessage?: Pick<ToasterToast, 'title' | 'description'>,
) => {
  if (isFormActionError(error)) {
    const errorContext = error?.context.validation;

    if (errorContext) {
      Object.entries(errorContext).forEach(([field, messages], index) => {
        const errorMessage = Array.isArray(messages) ? messages[0] : messages;
        form.setError(field as Path<TFormSchema>, {
          type: 'manual',
          message: errorMessage,
        }, { shouldFocus: index === 0 });
      });
    }
  } else {
    toast({
      title: errorToastMessage?.title || errorToString(error),
      description: (errorToastMessage?.title && !errorToastMessage?.description)
        ? errorToString(error)
        : errorToastMessage?.description,
      variant: 'destructive',
      duration: Infinity,
    });
  }
};

export const handleFormSubmit = async <
  TFormSchema extends FieldValues,
  R,
>(
  form: UseFormReturn<TFormSchema>,
  api: {
    submitFn: () => Promise<R>;
    toastSuccess?: boolean | Partial<Pick<ToasterToast, 'title' | 'description'>>
      | ((response: R) => Partial<Pick<ToasterToast, 'title' | 'description'>>);
    toastError?: Partial<Pick<ToasterToast, 'title' | 'description'>>;
    onSuccess?: (response: R) => Promise<void> | void;
    onError?: (error: ServerActionError | unknown) => Promise<void> | void;
    onSettled?: () => Promise<void> | void;
  },
): Promise<void> => {
  try {
    const response = await api.submitFn();

    if (api.toastSuccess) {
      const toastData = typeof api.toastSuccess === 'boolean'
        ? undefined
        : typeof api.toastSuccess === 'function'
          ? api.toastSuccess(response)
          : api.toastSuccess;
      const resData = isSuccessResponse(response) ? response : undefined;

      if (toastData?.title || toastData?.description || resData?.message) {
        toast({
          title: toastData?.title ?? 'Úspěch',
          description: toastData?.description ?? resData?.message,
          variant: 'success',
        });
      }
    }

    if (api.onSuccess) {
      await api.onSuccess(response);
    }
  } catch (error) {
    handleFormError<TFormSchema>(error, form, api.toastError);
    if (api.onError) {
      await api.onError(error);
    }
  } finally {
    if (api.onSettled) {
      await api.onSettled();
    }
  }
};

export function isFormActionError(error: unknown): error is FormActionError {
  return (
    typeof error === 'object'
    && error !== null
    && 'context' in error
    && typeof error.context === 'object'
    && error.context !== null
    && 'validation' in error.context
    && typeof error.context.validation === 'object'
    && error.context.validation !== null
  );
}

export function isSuccessResponse(response: unknown): response is Success {
  return (
    typeof response === 'object'
    && response !== null
    && 'status' in response
    && typeof response.status === 'string'
    && 'code' in response
    && typeof response.code === 'number'
    && 'message' in response
    && typeof response.message === 'string'
    && response.code.toString().startsWith('2')
  );
}
